import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Mail, ArrowRight, CheckCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API = `${window.location.origin}/api`;

const newsletterInterests = [
  { id: "endurance", label: "endurance" },
  { id: "concerts", label: "concerts" },
  { id: "nature", label: "nature" },
  { id: "famille", label: "famille" },
  { id: "pedagogie", label: "pedagogie" },
  { id: "benevole", label: "benevole" },
];

export default function NewsletterSection() {
  const { t } = useTranslation();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const toggleInterest = (id) => {
    setSelectedInterests(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email: newsletterEmail, interests: selectedInterests });
      setSubmitted(true);
    } catch (error) {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-fuchsia-600 via-fuchsia-700 to-purple-800 text-white" data-testid="newsletter-actualites-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center mb-8">
          <Mail className="h-10 w-10 mx-auto mb-4 text-lime-400" />
          <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl font-bold mb-3">{t('homeExt.joinCommunity')}</h2>
          <p className="text-white/90">{t('homeExt.newsletterDesc')}</p>
        </div>

        {submitted ? (
          <div className="text-center py-6">
            <CheckCircle className="h-12 w-12 mx-auto mb-3 text-lime-400" />
            <h3 className="text-xl font-bold mb-2">{t('homeExt.thankYou')}</h3>
            <p className="text-white/90">{t('homeExt.subscribedMsg')}</p>
            <Button variant="outline" className="mt-4 border-white/40 text-white hover:bg-white/10" onClick={() => { setSubmitted(false); setNewsletterEmail(''); setSelectedInterests([]); }}>
              Nouvelle inscription
            </Button>
          </div>
        ) : (
          <Card className="bg-white/10 backdrop-blur-sm border-0">
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label className="text-white mb-3 block">{t('homeExt.iWantInfo')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {newsletterInterests.map((interest) => (
                      <label key={interest.id} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${selectedInterests.includes(interest.id) ? 'bg-lime-500/30 border-2 border-lime-400' : 'bg-white/10 border-2 border-transparent hover:bg-white/20'}`}>
                        <input type="checkbox" checked={selectedInterests.includes(interest.id)} onChange={() => toggleInterest(interest.id)} className="w-4 h-4 rounded accent-lime-500" />
                        <span className="text-white text-sm">{t(`homeExt.nl${interest.label.charAt(0).toUpperCase() + interest.label.slice(1)}`)}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Input type="email" placeholder={t('homeExt.emailPlaceholder')} value={newsletterEmail} onChange={(e) => setNewsletterEmail(e.target.value)} required className="flex-1 bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-lime-400" data-testid="newsletter-email-actualites" />
                  <Button type="submit" disabled={loading} className="bg-lime-500 hover:bg-lime-400 text-white rounded-full px-6" data-testid="newsletter-submit-actualites">
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t('homeExt.joinCommunityBtn')}<ArrowRight className="ml-2 h-4 w-4" /></>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </section>
  );
}
